import {
  createFrontendPlugin,
  createRouteRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import ListIcon from '@material-ui/icons/List';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CheckIcon from '@material-ui/icons/Check';
import DnsIcon from '@material-ui/icons/Dns';
import InboxIcon from '@material-ui/icons/Inbox';
import PeopleIcon from '@material-ui/icons/People';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';

const myVmsRouteRef = createRouteRef();
const provisionVmRouteRef = createRouteRef();
const requestsRouteRef = createRouteRef();
const myRequestsRouteRef = createRouteRef();
const allRequestsRouteRef = createRouteRef();
const approvalsRouteRef = createRouteRef();
const requestDetailRouteRef = createRouteRef();
const approvalReviewRouteRef = createRouteRef();
const instanceDetailRouteRef = createRouteRef();
const accessRequestRouteRef = createRouteRef();
const userManagementRouteRef = createRouteRef();
const changeRequestRouteRef = createRouteRef();
const justificationRouteRef = createRouteRef();
const vmGovernanceRouteRef = createRouteRef();
const vmGovernanceUserRouteRef = createRouteRef();

const myVmsPage = PageBlueprint.make({
  name: 'my-vms',
  params: {
    path: '/infrastructure/my-vms',
    routeRef: myVmsRouteRef,
    title: 'Virtual Machines',
    icon: <DnsIcon />,
    // The framework's default page chrome (icon + title bar above our own
    // InfraHeader) renders with a lighter background than the rest of the
    // page and isn't themeable — every page supplies its own header instead.
    noHeader: true,
    loader: () => import('./pages/YourVmsPage').then(m => <m.YourVmsPage />),
  },
});

// A fully custom wizard — not Backstage's generic scaffolder UI — so it can
// show a live, cross-step "Configuration Summary" sidebar that the generic
// scaffolder Stepper has no supported extension point for (its formData
// lives in purely internal component state). It still submits into the
// exact same templates/oci-vm/template.yaml via scaffolderApiRef.scaffold()
// on the Review step, so the backend actions/approval/provisioning pipeline
// is unchanged — only the form-filling UI is bespoke.
const provisionVmPage = PageBlueprint.make({
  name: 'provision-vm',
  params: {
    path: '/infrastructure/provision-vm',
    routeRef: provisionVmRouteRef,
    title: 'Provision VM',
    icon: <AddCircleOutlineIcon />,
    noHeader: true,
    loader: () =>
      import('./pages/ProvisionVmWizardPage').then(m => (
        <m.ProvisionVmWizardPage />
      )),
  },
});

// Combines VM provisioning requests and Keep VM Running extension requests
// into one tracking page — supersedes My Requests / All Requests (below)
// and the runtime-extension plugin's own "Keep VM Running Requests" page
// as the primary UI. Those routes are kept registered (nothing in the
// sidebar links to them anymore) rather than deleted, in case anything
// still deep-links to one of their URLs.
const requestsPage = PageBlueprint.make({
  name: 'requests',
  params: {
    path: '/infrastructure/requests',
    routeRef: requestsRouteRef,
    title: 'Requests',
    icon: <InboxIcon />,
    noHeader: true,
    loader: () => import('./pages/RequestsPage').then(m => <m.RequestsPage />),
  },
});

// Superseded by the combined Requests page above — Sidebar.tsx no longer
// takes this page's nav item, but the route/page itself is left working
// for anyone with an old link to it.
const myRequestsPage = PageBlueprint.make({
  name: 'my-requests',
  params: {
    path: '/infrastructure/my-requests',
    routeRef: myRequestsRouteRef,
    title: 'My Requests',
    icon: <ListIcon />,
    noHeader: true,
    loader: () =>
      import('./pages/MyRequestsPage').then(m => <m.MyRequestsPage />),
  },
});

// Superseded by the combined Requests page (which already shows every
// request when the caller has request.read.all) — see myRequestsPage.
const allRequestsPage = PageBlueprint.make({
  name: 'all-requests',
  params: {
    path: '/infrastructure/all-requests',
    routeRef: allRequestsRouteRef,
    title: 'All Requests',
    icon: <PeopleIcon />,
    noHeader: true,
    loader: () =>
      import('./pages/AllRequestsPage').then(m => <m.AllRequestsPage />),
  },
});

// Combined "Pending Approvals" + "Approval History" tabs live inside this
// one page now — there used to be a second route (Approval History) that
// rendered the same content with a different tab pre-selected; removed as
// redundant once both tabs already live in one page.
const approvalsPage = PageBlueprint.make({
  name: 'approvals',
  params: {
    path: '/infrastructure/approvals',
    routeRef: approvalsRouteRef,
    title: 'Approvals',
    icon: <CheckIcon />,
    noHeader: true,
    loader: () =>
      import('./pages/ApprovalsPage').then(m => <m.ApprovalsPage />),
  },
});

const requestDetailPage = PageBlueprint.make({
  name: 'request-detail',
  params: {
    path: '/infrastructure/requests/:requestId',
    routeRef: requestDetailRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/RequestDetailPage').then(m => <m.RequestDetailPage />),
  },
});

// The approver's dedicated review screen for a VM provisioning request —
// distinct from requestDetailPage above, which is what the *requester*
// sees for their own submitted request. Reached from the Approval Queue.
const approvalReviewPage = PageBlueprint.make({
  name: 'approval-review',
  params: {
    path: '/infrastructure/approvals/:requestId',
    routeRef: approvalReviewRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/ApprovalReviewPage').then(m => <m.ApprovalReviewPage />),
  },
});

const accessRequestPage = PageBlueprint.make({
  name: 'access-request',
  params: {
    path: '/infrastructure/access-requests/:requestId',
    routeRef: accessRequestRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/AccessRequestPage').then(m => <m.AccessRequestPage />),
  },
});

const changeRequestPage = PageBlueprint.make({
  name: 'change-request',
  params: {
    path: '/infrastructure/change-requests/:requestId',
    routeRef: changeRequestRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/ChangeRequestPage').then(m => <m.ChangeRequestPage />),
  },
});

const justificationPage = PageBlueprint.make({
  name: 'justification',
  params: {
    path: '/infrastructure/justifications/:requestId',
    routeRef: justificationRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/JustificationPage').then(m => <m.JustificationPage />),
  },
});

// Super admins only — the page guards itself on the vm.governance permission
// and the backend enforces it again on every call.
const vmGovernancePage = PageBlueprint.make({
  name: 'vm-governance',
  params: {
    path: '/infrastructure/governance',
    routeRef: vmGovernanceRouteRef,
    title: 'VM Governance',
    icon: <VerifiedUserIcon />,
    noHeader: true,
    loader: () =>
      import('./pages/VmGovernancePage').then(m => <m.VmGovernancePage />),
  },
});

const vmGovernanceUserPage = PageBlueprint.make({
  name: 'vm-governance-user',
  params: {
    path: '/infrastructure/governance/users/:email',
    routeRef: vmGovernanceUserRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/VmGovernanceUserPage').then(m => (
        <m.VmGovernanceUserPage />
      )),
  },
});

const instanceDetailPage = PageBlueprint.make({
  name: 'instance-detail',
  params: {
    path: '/infrastructure/instances/:instanceId',
    routeRef: instanceDetailRouteRef,
    noHeader: true,
    loader: () =>
      import('./pages/InstanceDetailPage').then(m => <m.InstanceDetailPage />),
  },
});

const userManagementPage = PageBlueprint.make({
  name: 'user-management',
  params: {
    path: '/infrastructure/users',
    routeRef: userManagementRouteRef,
    title: 'User Management',
    icon: <PeopleOutlineIcon />,
    noHeader: true,
    loader: () =>
      import('./pages/UserManagementPage').then(m => <m.UserManagementPage />),
  },
});

export default createFrontendPlugin({
  pluginId: 'infrastructure',
  routes: {
    myVms: myVmsRouteRef,
    provisionVm: provisionVmRouteRef,
    requests: requestsRouteRef,
    myRequests: myRequestsRouteRef,
    allRequests: allRequestsRouteRef,
    approvals: approvalsRouteRef,
    requestDetail: requestDetailRouteRef,
    approvalReview: approvalReviewRouteRef,
    instanceDetail: instanceDetailRouteRef,
    accessRequest: accessRequestRouteRef,
    userManagement: userManagementRouteRef,
    changeRequest: changeRequestRouteRef,
    justification: justificationRouteRef,
    vmGovernance: vmGovernanceRouteRef,
    vmGovernanceUser: vmGovernanceUserRouteRef,
  },
  extensions: [
    myVmsPage,
    provisionVmPage,
    requestsPage,
    myRequestsPage,
    allRequestsPage,
    approvalsPage,
    requestDetailPage,
    approvalReviewPage,
    instanceDetailPage,
    accessRequestPage,
    userManagementPage,
    changeRequestPage,
    justificationPage,
    vmGovernancePage,
    vmGovernanceUserPage,
  ],
});
